import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FileText, Play, CheckCircle, AlertTriangle, Activity, Settings2, Download, Zap, ServerOff, History, Terminal, X, Loader2, Pencil, Check, Trash2 } from 'lucide-react';
import { ExecutionStatus, ToastState } from './types';
import RtcCharts from './RtcCharts';
import * as XLSX from 'xlsx';

const _normalizeApiBase = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (raw.startsWith(':')) {
    return `${window.location.protocol}//${window.location.hostname}${raw}`;
  }
  if (raw.startsWith('//')) {
    return `${window.location.protocol}${raw}`;
  }
  if (raw.startsWith('/')) {
    return `${window.location.origin}${raw}`;
  }

  return raw.replace(/\/+$/, '');
};

// API Configuration
const API_BASE =
  _normalizeApiBase((import.meta as any).env?.VITE_API_BASE || '') ||
  _normalizeApiBase((window as any).__API_BASE__ || '') ||
  window.location.origin;
const ACCEPTED_UPLOAD_TYPES = '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const _rtcInFlightRequests = new Map<string, Promise<any>>();
const _rtcResultCache = new Map<string, { data: any; expiresAt: number }>();
const _RTC_CACHE_TTL_MS = 7000;

const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp || 0);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

const getStoredRtcToken = (): string => {
  return String(
    sessionStorage.getItem('access_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('auth_token') || ''
  ).trim();
};

const hasAuthSession = (): boolean => {
  const access = getStoredRtcToken();
  return !!access && !isTokenExpired(access);
};

const dedupJsonFetch = async (key: string, url: string, options?: RequestInit, ttlMs: number = _RTC_CACHE_TTL_MS): Promise<any> => {
  const cached = _rtcResultCache.get(key);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data;
  }

  const inFlight = _rtcInFlightRequests.get(key);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = (async () => {
    const res = await fetch(url, options);
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      const error: any = new Error(errBody || `HTTP ${res.status}`);
      error.status = res.status;
      throw error;
    }
    const data = await res.json();
    _rtcResultCache.set(key, { data, expiresAt: Date.now() + ttlMs });
    return data;
  })();

  _rtcInFlightRequests.set(key, requestPromise);
  try {
    return await requestPromise;
  } finally {
    _rtcInFlightRequests.delete(key);
  }
};

const getAuthHeaders = (siteCode?: string): Record<string, string> => {
  const token = getStoredRtcToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const effectiveSiteCode =
    siteCode ||
    sessionStorage.getItem('tenant') ||
    localStorage.getItem('currentSite') ||
    '';
  const effectiveSiteId =
    sessionStorage.getItem('site_id') ||
    localStorage.getItem('site_id') ||
    '';
  if (effectiveSiteCode) headers['X-Site-Code'] = String(effectiveSiteCode);
  if (effectiveSiteId) headers['X-Site-Id'] = String(effectiveSiteId);
  return headers;
};

interface RTCHistory {
  id: number;
  run_id: string;
  status: string;
  progress_pct: number;
  total_days: number;
  started_at: string | null;
  completed_at: string | null;
  error_message: string;
}

interface RtcSimulationSectionProps {
  siteCode?: string;
  onToast: (toast: ToastState) => void;
  setStatus: React.Dispatch<React.SetStateAction<ExecutionStatus>>;
  status: ExecutionStatus;
  /** When set (by an admin), show history/charts for this user instead of the current user */
  viewUserId?: number;
  actionsDisabled?: boolean;
  tokenCost?: number;
  tokenBalance?: number | null;
  onTokenBalanceChange?: (nextBalance: number) => void;
}

const RtcSimulationSection: React.FC<RtcSimulationSectionProps> = ({ siteCode, onToast, setStatus, status, viewUserId, actionsDisabled, tokenCost = 2, tokenBalance = null, onTokenBalanceChange }) => {
  const [configId, setConfigId] = useState<number | null>(null);
  const [fileRaw, setFileRaw] = useState<File | null>(null);
  const [exchangeStartDate, setExchangeStartDate] = useState<string>('');
  const [exchangeEndDate, setExchangeEndDate] = useState<string>('');
  const [recentRawName, setRecentRawName] = useState<string | null>(null);
  const [recentDamName, setRecentDamName] = useState<string | null>(null);
  const [history, setHistory] = useState<RTCHistory[]>([]);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const [fillMode, setFillMode] = useState<'custom' | 'recent'>('custom');
  const [chartData, setChartData] = useState<any>(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [elapsedTimeStr, setElapsedTimeStr] = useState<string>('');
  const initialChartLoaded = useRef(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [terminalLogs, setTerminalLogs] = useState<string>('');
  const logsContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (logsContainerRef.current && isSidebarOpen) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [terminalLogs, isSidebarOpen]);

  const defaultValues: Record<string, string> = {
    bsoc_end_mwh: '0',
    generation_100_mw: '100',
    penalty_band_pct: '50',
    plf_meet_pct: '85',
    current_plant_operational_pct: '40',
    battery_cycle_duration_hr: '6',
    current_battery_operational_mw: '35',
    max_discharge_mw: '65',
    extra_charging_factor_pct: '5',
    charging_loss_factor_pct: '29.75',
    min_charge_mw: '5.2',
    min_discharge_pct: '10',
    max_charge_mw: '54.6',
    gt_100: '4.25',
    gt_115: '3.5',
    last_x_time_block: '4',
    rev_sharing_factor_pct: '50',
    prev_day_battery_profile: '[0.0, ...]'
  };

  const [inputs, setInputs] = useState<Record<string, string>>(Object.keys(defaultValues).reduce((acc, key) => { acc[key] = ''; return acc; }, {} as Record<string, string>));
  const rawFileInputRef = useRef<HTMLInputElement>(null);

  const formatDateForApi = (value: string): string => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    const parts = trimmed.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return trimmed;
  };

  const labelMapArray: {key: string, label: string}[] = [
    { key: 'generation_100_mw', label: 'RTC CONTRACTUAL CAP (MW)' },
    { key: 'penalty_band_pct', label: 'MIN PLF PER 15MIN (%)' },
    { key: 'plf_meet_pct', label: 'YEARLY PLF (%)' },
    { key: 'current_plant_operational_pct', label: 'PLANT OP. CAP (%)' },
    { key: 'battery_cycle_duration_hr', label: 'BATTERY CYCLE (HR)' },
    { key: 'current_battery_operational_mw', label: 'BATTERY OP. CAP (MW)' },
    { key: 'max_discharge_mw', label: 'MAX DISCHARGE/15M (MW)' },
    { key: 'extra_charging_factor_pct', label: 'EXTRA CHARGE FACT (%)' },
    { key: 'charging_loss_factor_pct', label: 'CHARGE LOSS FACT (%)' },
    { key: 'min_charge_mw', label: 'MIN CHARGE/15M (MW)' },
    { key: 'min_discharge_pct', label: 'MIN DISCHARGE/15M (%)' },
    { key: 'max_charge_mw', label: 'MAX CHARGE/15M (MW)' },
    { key: 'gt_100', label: 'GRID TARIFF <= 100%' },
    { key: 'gt_115', label: 'GRID TARIFF > 100%' },
    { key: 'last_x_time_block', label: 'TIME BLOCKS PRESERVE' },
    { key: 'rev_sharing_factor_pct', label: 'REV SHARE FACT (%)' },
    { key: 'bsoc_end_mwh', label: 'INITIAL BSOC END (MWh)' }
  ];

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileValues, setProfileValues] = useState<string[]>([]);
  const [bulkInput, setBulkInput] = useState('');

  const DEFAULT_BATTERY_PROFILE = [
    0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
    0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
    0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 1.979, 1.978,
    1.969, 1.956, 7.792, 1.939, 8.155, 8.581, 8.803, 9.018, 8.87, 8.449, 8.357, 8.735,
    8.423, 2.023, 7.779, 1.867, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, -1.625, -1.625,
    -2.03, -2.883, -3.74, -3.953, -4.027, -4.08, -4.133, -4.058, -3.955, -3.878, -3.798,
    -3.9, -4.025, -4.128, -4.228, -4.233, -4.235, -4.24, -4.245, -4.23, -4.215, -4.203,
    -16.25
  ];

  useEffect(() => {
    if (isProfileModalOpen) {
      const raw = inputs.prev_day_battery_profile || '';
      try {
        const cleaned = raw.replace(/[\[\]]/g, '').trim();
        if (cleaned) {
          const parts = cleaned.split(',').map(v => v.trim());
          const newVals = Array(96).fill('0.0');
          for (let i = 0; i < 96; i++) {
            if (i < parts.length) {
              const num = parseFloat(parts[i]);
              newVals[i] = isNaN(num) ? '0.0' : String(num);
            }
          }
          setProfileValues(newVals);
        } else {
          setProfileValues(DEFAULT_BATTERY_PROFILE.map(String));
        }
      } catch (e) {
        setProfileValues(DEFAULT_BATTERY_PROFILE.map(String));
      }
      setBulkInput('');
    }
  }, [isProfileModalOpen, inputs.prev_day_battery_profile]);

  const handleSaveProfile = () => {
    const formatted = `[${profileValues.map(v => {
      const num = parseFloat(v);
      return isNaN(num) ? '0.0' : String(num);
    }).join(', ')}]`;
    
    setInputs(prev => ({
      ...prev,
      prev_day_battery_profile: formatted
    }));
    setIsProfileModalOpen(false);
  };

  const profileFileInputRef = useRef<HTMLInputElement>(null);

  const parseProfileContent = (content: string): string[] => {
    let numbers: number[] = [];
    const trimmed = content.trim();

    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          numbers = parsed.map(item => {
            if (typeof item === 'number') return item;
            if (typeof item === 'string') return parseFloat(item);
            if (typeof item === 'object' && item !== null) {
              const val = item.value ?? item.val ?? item.v ?? Object.values(item)[0];
              return parseFloat(String(val));
            }
            return 0;
          });
        } else if (typeof parsed === 'object' && parsed !== null) {
          const list = parsed.values || parsed.profile || parsed.data || Object.values(parsed);
          if (Array.isArray(list)) {
            numbers = list.map(item => typeof item === 'number' ? item : parseFloat(String(item)));
          }
        }
      } catch (e) {
        // Fall back to text parsing
      }
    }

    if (numbers.length === 0) {
      const cleaned = content.replace(/[\[\]]/g, '');
      const parts = cleaned.split(/[\s,;\t\n\r]+/).filter(Boolean);
      numbers = parts.map(p => parseFloat(p)).filter(n => !isNaN(n));
    }

    const newVals = Array(96).fill('0.0');
    for (let i = 0; i < 96; i++) {
      if (i < numbers.length && !isNaN(numbers[i])) {
        newVals[i] = String(numbers[i]);
      }
    }
    return newVals;
  };

  const handleProfileFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();

    // Handle Excel files (.xlsx, .xls)
    if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = event.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rows: any[] = XLSX.utils.sheet_to_json(sheet);

          const newVals = Array(96).fill('0.0');
          rows.forEach((row: any, i: number) => {
            if (i < 96) {
              // Try VALUE column first, then fallback to 3rd column
              const val = row['VALUE'] ?? row['value'] ?? row['Value'] ?? Object.values(row)[2];
              const num = parseFloat(String(val));
              newVals[i] = isNaN(num) ? '0.0' : String(num);
            }
          });

          setProfileValues(newVals);
          onToast({
            show: true,
            type: 'success',
            title: 'Excel Uploaded',
            message: `Loaded ${Math.min(rows.length, 96)} values from ${file.name}`
          });
        } catch (err) {
          onToast({
            show: true,
            type: 'error',
            title: 'Upload Error',
            message: 'Failed to read Excel file. Please use the downloaded template format.'
          });
        }
      };
      reader.readAsArrayBuffer(file);
      if (e.target) e.target.value = '';
      return;
    }

    // Handle text-based files (.json, .txt, .csv)
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        if (typeof text === 'string') {
          const newVals = parseProfileContent(text);
          setProfileValues(newVals);
          onToast({
            show: true,
            type: 'success',
            title: 'File Uploaded',
            message: `Successfully loaded 96 timeblock values from ${file.name}`
          });
        }
      } catch (err) {
        onToast({
          show: true,
          type: 'error',
          title: 'Upload Error',
          message: 'Failed to read file. Please ensure it is a valid file.'
        });
      }
    };
    reader.readAsText(file);
    if (e.target) e.target.value = '';
  };

  const handleBulkImport = () => {
    try {
      const cleaned = bulkInput.replace(/[\[\]]/g, '').trim();
      const parts = cleaned.split(/[\s,;\t\n]+/).filter(Boolean);
      if (parts.length === 0) return;

      const newVals = [...profileValues];
      for (let i = 0; i < 96; i++) {
        if (i < parts.length) {
          const num = parseFloat(parts[i]);
          newVals[i] = isNaN(num) ? '0.0' : String(num);
        }
      }
      setProfileValues(newVals);
      setBulkInput('');
    } catch (e) {
    }
  };

  const reportError = useCallback((title: string, message: string, openSidebar = true) => {
    const errorLogLine = `[ERROR] ${title}: ${message}`;
    setTerminalLogs(prev => {
      if (!prev) return errorLogLine;
      if (prev.endsWith(errorLogLine)) return prev;
      return `${prev}\n${errorLogLine}`;
    });
    if (openSidebar) {
      setIsSidebarOpen(true);
    }
  }, []);

  const fetchChartData = async (runId: string, forceFresh = false) => {
    if (!hasAuthSession()) {
      return;
    }
    setLoadingCharts(true);
    setSelectedRunId(runId);
    try {
      const userParam = viewUserId ? `&view_user_id=${viewUserId}` : '';
      const url = `${API_BASE}/api/v1/rtc-simulation/graph-data/?run_id=${runId}${userParam}`;
      const key = `rtc:graph:${siteCode || ''}:${viewUserId || 'self'}:${runId}`;
      if (forceFresh) {
        _rtcResultCache.delete(key);
      }
      const data = await dedupJsonFetch(key, url, { headers: getAuthHeaders(siteCode) }, forceFresh ? 0 : 5000);
      if (data?.status === 'success') {
        setChartData(data);
      } else {
        reportError('Chart Data Failed', 'Could not load simulation results.', true);
      }
    } catch (e: any) {
      if (e?.status === 401) {
        reportError('Session Expired', 'Please login again to access simulation data.', true);
        return;
      }
      reportError('Connection Error', 'Backend unreachable.', true);
    } finally { setLoadingCharts(false); }
  };

  const fetchLogs = async (runId: string) => {
    if (!hasAuthSession()) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/rtc-simulation/logs/?run_id=${encodeURIComponent(runId)}`, { headers: getAuthHeaders(siteCode) });
      if (res.ok) {
        const logData = await res.json();
        if (logData.status === 'success' && logData.logs) {
          setTerminalLogs(logData.logs);
          return;
        }
      }
      setTerminalLogs('[INFO] No logs found for this run.');
    } catch (e) {
      setTerminalLogs('[ERROR] Failed to fetch logs for this run.');
    }
  };

  const handleRunClick = (run: RTCHistory) => {
    setSelectedRunId(run.run_id);
    setIsSidebarOpen(true);
    setTerminalLogs(`Fetching logs for run ${run.run_id}...\n`);
    fetchLogs(run.run_id);

    if (run.status === 'completed') {
      fetchChartData(run.run_id);
      setStatus({ status: 'completed', progress: 100 });
    } else if (run.status === 'failed') {
      setChartData(null);
      setStatus({ status: 'failed', progress: 0 });
    } else if (run.status === 'running') {
      setChartData(null);
      setStatus({ status: 'running', progress: run.progress_pct || 0 });
      startPolling(run.run_id);
    } else {
      setChartData(null);
      setStatus({ status: run.status as any, progress: 0 });
    }
  };

  const loadConfig = async () => {
    if (!hasAuthSession()) {
      return;
    }
    try {
      const key = `rtc:config:${siteCode || ''}:${viewUserId || 'self'}`;
      const data = await dedupJsonFetch(
        key,
        `${API_BASE}/api/v1/rtc-simulation/config/`,
        { headers: getAuthHeaders(siteCode) }
      );
      if (data?.status === 'success' && data.config) {
        const c = data.config;
        setConfigId(c.id);
        const newInputs = { ...inputs };
        Object.keys(defaultValues).forEach(key => { if (c[key] !== undefined && c[key] !== null) newInputs[key] = String(c[key]); });
        setInputs(newInputs);
        if (c.raw_file_path) setRecentRawName(String(c.raw_file_path).split(/[/\\]/).pop() || null);
        if (c.dam_market_file_path) setRecentDamName(String(c.dam_market_file_path).split(/[/\\]/).pop() || null);
      }
    } catch (e: any) {
      if (e?.status === 401) {
        reportError('Session Expired', 'Please login again to load simulation configuration.', false);
        return;
      }
      reportError('Service Error', 'Backend unreachable.', false);
    }
  };

  const loadHistory = async (autoLoadChart = false) => {
    if (!hasAuthSession()) {
      return;
    }
    try {
      const userParam = viewUserId ? `?view_user_id=${viewUserId}` : '';
      const key = `rtc:history:${siteCode || ''}:${viewUserId || 'self'}`;
      const payload = await dedupJsonFetch(
        key,
        `${API_BASE}/api/v1/rtc-simulation/history/${userParam}`,
        { headers: getAuthHeaders(siteCode) }
      );
      const runs = payload?.runs || [];
      setHistory(runs);

      if (autoLoadChart && !initialChartLoaded.current && runs.length > 0) {
        if (runs[0].status === 'running') {
          setSelectedRunId(runs[0].run_id);
          setStatus({ status: 'running', progress: runs[0].progress_pct || 0 });
          startPolling(runs[0].run_id);
          fetchLogs(runs[0].run_id);
          setIsSidebarOpen(true);
          initialChartLoaded.current = true;
        } else {
          const completed = runs.filter((r: any) => r.status === 'completed');
          if (completed.length > 0) {
            setSelectedRunId(completed[0].run_id);
            fetchChartData(completed[0].run_id);
            fetchLogs(completed[0].run_id);
            initialChartLoaded.current = true;
          }
        }
      }
    } catch (e) {}
  };
  useEffect(() => {
    initialChartLoaded.current = false;
    setSelectedRunId(null);
    setChartData(null);
    loadConfig();
    loadHistory(true);
    return () => stopPolling();
  }, [viewUserId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => { setInputs(prev => ({ ...prev, [e.target.name]: e.target.value })); };

  const exportRunCsv = async (runId: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/rtc-simulation/export-csv/?run_id=${encodeURIComponent(runId)}`, {
        headers: getAuthHeaders(siteCode)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Export failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fallbackName = `day_0_inpt_${runId}.csv`;
      const header = res.headers.get('content-disposition') || '';
      const headerName = header.match(/filename="?([^";]+)"?/i)?.[1];
      a.href = url;
      a.download = headerName || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      onToast({ show: true, type: 'success', title: 'Exported', message: `CSV exported for ${runId}` });
    } catch (e: any) {
      reportError('Export Failed', e?.message || 'Could not export CSV.', true);
    }
  };

  const deleteRun = async (runId: string) => {
    if (!hasAuthSession()) return;
    if (!window.confirm(`Are you sure you want to delete simulation run: ${runId}?`)) {
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/v1/rtc-simulation/delete/`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(siteCode),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ run_id: runId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success') {
          onToast({ show: true, type: 'success', title: 'Deleted', message: `Run ${runId} successfully deleted.` });
          if (selectedRunId === runId) {
            setSelectedRunId(null);
            setChartData(null);
            setTerminalLogs('');
            setStatus({ status: 'idle', progress: 0 });
          }
          loadHistory(true);
        } else {
          throw new Error(data.error || 'Failed to delete run');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete run');
      }
    } catch (e: any) {
      reportError('Delete Failed', e?.message || 'Could not delete run.', true);
    }
  };

  const handleRunSimulation = async () => {
    const hasTokens = tokenBalance === null ? true : tokenBalance >= tokenCost;
    if (actionsDisabled) {
      reportError('Execution Not Allowed', 'Only HO Admin can execute simulation tabs.', true);
      return;
    }
    if (!hasTokens) {
      reportError('Insufficient Tokens', `Required: ${tokenCost}, Available: ${tokenBalance ?? 0}`, true);
      return;
    }

    // Set immediate loading state
    setTerminalLogs('Initiating sequence...\n');
    setStatus({ status: 'running', progress: 0 });
    setElapsedTimeStr('00:00');
    setSelectedRunId('Initializing...');
    setIsSidebarOpen(true);
    setChartData(null);

    try {
      const finalInputs = { ...inputs };
      Object.keys(defaultValues).forEach(key => { if (!finalInputs[key]) finalInputs[key] = defaultValues[key]; });

      const formData = new FormData();
      Object.entries(finalInputs).forEach(([k, v]) => formData.append(k, v));
      if (configId) formData.append('config_id', String(configId));
      if (fileRaw) formData.append('file_raw', fileRaw);

      const normalizedExchangeStart = formatDateForApi(exchangeStartDate);
      const normalizedExchangeEnd = formatDateForApi(exchangeEndDate);
      if (!normalizedExchangeStart || !normalizedExchangeEnd) {
        reportError('Date Range Required', 'Please select Exchange Rate start and end dates.', true);
        setStatus({ status: 'failed', progress: 0 });
        return;
      }
      formData.append('exchange_start_date', normalizedExchangeStart);
      formData.append('exchange_end_date', normalizedExchangeEnd);

      const res = await fetch(`${API_BASE}/api/v1/rtc-simulation/run/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(siteCode) }, 
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setSelectedRunId(data.run_id);
        if (typeof data?.tokens_remaining === 'number' && onTokenBalanceChange) {
          onTokenBalanceChange(data.tokens_remaining);
        }
        if (data.config_id) setConfigId(data.config_id);
        startPolling(data.run_id);
      } else {
        const err = await res.json();
        reportError('Start Failed', err.error || 'Server rejected the request.', true);
        setStatus({ status: 'failed', progress: 0 });
      }
    } catch (e: any) {
      reportError('Network Error', 'Could not reach simulation engine.', true);
      setStatus({ status: 'failed', progress: 0 });
    }
  };

  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); };

  const startPolling = (runId: string) => {
    stopPolling();
    const startTime = Date.now();
    
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/rtc-simulation/status/?run_id=${runId}`, { headers: getAuthHeaders(siteCode) });
        let elapsedSecs = Math.floor((Date.now() - startTime) / 1000);
        
        // Fetch logs (Optimized: separately from status)
        fetch(`${API_BASE}/api/v1/rtc-simulation/logs/?run_id=${runId}`, { headers: getAuthHeaders(siteCode) })
          .then(logRes => logRes.json())
          .then(logData => {
            if (logData.status === 'success' && logData.logs) {
              setTerminalLogs(prev => {
                const newLogs = logData.logs;
                if (prev === newLogs) return prev;
                return newLogs;
              });

              if (logData.logs.includes('Simulation completed successfully.')) {
                stopPolling();
                setStatus({ status: 'completed', progress: 100 });
                loadHistory();
                fetchChartData(runId, true);
              } else if (logData.logs.includes('Simulation failed.')) {
                stopPolling();
                setStatus({ status: 'failed', progress: 0 });
                loadHistory();
              }
            }
          })
          .catch(() => {});
        
        const formatTime = (s: number) => {
          const m = String(Math.floor(s / 60)).padStart(2, '0');
          const sec = String(s % 60).padStart(2, '0');
          return `${m}:${sec}`;
        };
        if (res.ok) {
          const data = await res.json();

          if (typeof data.execution_time_ms === 'number' && data.execution_time_ms > 0) {
            elapsedSecs = Math.max(0, Math.floor(data.execution_time_ms / 1000));
          } else if (data.started_at) {
            const startMs = new Date(data.started_at).getTime();
            if (!isNaN(startMs)) {
              const endMs = data.completed_at ? new Date(data.completed_at).getTime() : Date.now();
              elapsedSecs = Math.max(0, Math.floor((endMs - startMs) / 1000));
            }
          }

          const progress = data.progress_pct || 0;
          let displayTime = formatTime(elapsedSecs);
          
          if (progress > 2 && progress < 100 && data.status === 'running') {
            const totalEst = Math.floor((elapsedSecs / progress) * 100);
            displayTime = `${formatTime(elapsedSecs)} / ${formatTime(totalEst)} est.`;
          }
 
          if (data.status === 'completed' || data.status === 'failed') {
            stopPolling();
            setStatus({ status: data.status, progress: data.status === 'completed' ? 100 : 0 });
            setElapsedTimeStr(formatTime(elapsedSecs));
            loadHistory(); // Reload history at end
            if (data.status === 'completed') {
              fetchChartData(runId, true);
            } else {
              reportError('Simulation Failed', data.error_message || runId, true);
            }
          } else {
            setStatus({ status: 'running', progress: progress });
            setElapsedTimeStr(displayTime);
          }
        }
      } catch (e) {}
    }, 1000);
  };

  return (
    <div className="flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
      <div className="border-b border-gray-200 bg-gray-50 p-1.5 px-3 flex justify-between items-center z-10">
        <h2 className="text-sm font-extrabold text-gray-950 flex items-center gap-2" style={{ fontWeight: 1000, color: '#16202B' }}>
          <img src="/rtc-optimization-logo.png" alt="RTC Logo" className="w-[26px] h-auto object-contain mix-blend-multiply" /> Run RTC Optimization
        </h2>
      </div>

      {/* Floating Toggle Icon (visible at all times in the header right) */}
      <button 
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={`absolute top-0.5 right-3 z-[60] p-1.5 rounded-md transition-all duration-300 shadow-sm border ${isSidebarOpen ? 'bg-slate-800 text-white border-slate-700 hover:bg-slate-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-emerald-600'}`}
        title="Toggle Terminal Logs"
      >
        <Terminal className="w-4 h-4" />
      </button>

      {/* Terminal Logs Sidebar Overlay - Styled like ChatGPT, height matches chart section + header */}
      <div 
        className={`absolute top-0 right-0 w-[280px] h-[651px] bg-[#0D2747] border-b border-l border-slate-700 shadow-2xl transition-transform duration-300 ease-in-out z-50 flex flex-col rounded-l-xl rounded-tr-xl overflow-hidden ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between p-2.5 px-3 bg-[#0D2747] border-b border-[#1C4D88] shrink-0 z-10 min-h-[41px]">
          <div className={`transition-opacity duration-300 flex items-center gap-2 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'} overflow-hidden`}>
            <span className="text-[13px] font-bold text-white tracking-wide whitespace-nowrap truncate max-w-[220px]">
              {status.status === 'completed' && selectedRunId ? `Simulation Completed (${elapsedTimeStr})` : 
               status.status === 'failed' && selectedRunId ? `Simulation Failed (${elapsedTimeStr})` : 
               status.status === 'running' && selectedRunId ? `Simulation Running (${elapsedTimeStr})...` : 
               'Console'}
            </span>
          </div>
          {/* Empty spacer so the floating absolute toggle button has room here */}
          <div className="w-8 h-6 shrink-0"></div>
        </div>
        
        <div ref={logsContainerRef} className={`flex-1 p-2 pt-6 overflow-y-auto custom-scrollbar bg-[#0D2747] transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'}`} style={{ scrollBehavior: 'smooth' }}>
          <div className="flex-1 min-w-0 font-sans text-[13px] text-slate-300 leading-[1.6] pb-10">
            {terminalLogs ? (
              (() => {
                const lines = terminalLogs.split('\n').filter(line => line.trim());
                const isFinishedLog = terminalLogs.includes('Simulation completed successfully.') || terminalLogs.includes('Simulation failed.');
                return (
                  <div className="flex flex-col space-y-1.5">
                    {lines.slice(-200).map((line, idx) => {
                      const isProgress = line.includes('[PROGRESS]');
                      const isWarning = line.toLowerCase().includes('warning') || line.toLowerCase().includes('error');
                      const isLast = idx === Math.min(lines.length, 200) - 1;
                      const isRunning = status.status === 'running' && !isFinishedLog;
                      
                      return (
                        <div key={idx} className={`font-mono text-[11px] px-3 py-1.5 rounded-sm break-words border-l-2 transition-all duration-300 ${isProgress ? 'border-emerald-500 bg-emerald-500/5 text-emerald-300 font-bold' : isWarning ? 'border-red-500 bg-red-500/5 text-red-300' : 'border-slate-600/30 text-slate-400'}`}>
                          {line}
                          {isLast && isRunning && (
                            <span className="inline-block w-1.5 h-3.5 bg-emerald-400 ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
                          )}
                        </div>
                      );
                    })}
                    {status.status === 'running' && !isFinishedLog && (
                      <div className="flex items-center gap-2 px-3 py-2 mt-2 font-mono text-[10px] text-emerald-400/80 italic">
                        <div className="w-3 h-3 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                        Generating response...
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <div className="flex flex-col gap-3">
                {status.status === 'running' ? (
                  <div className="bg-[#1C4D88]/40 text-slate-400 px-3 py-3 rounded-lg shadow-sm font-sans animate-pulse border border-slate-700/50 flex items-center gap-3">
                    <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin"></div>
                    <span className="text-[12px]">Initiating sequence and waiting for detailed output...</span>
                  </div>
                ) : (
                  <div className="bg-[#1C4D88]/30 text-slate-400 px-3 py-3 rounded-lg shadow-sm font-sans border border-slate-700/50 flex items-center gap-3">
                    <Terminal className="w-4 h-4 text-amber-400/80 shrink-0" />
                    <span className="text-[12px] font-mono">Console Ready. Click "Run Simulation" to start.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-2 bg-gray-50/50">
        <div className="flex flex-col lg:flex-row gap-0.5 items-start">
          
          {/* LEFT COLUMN: Parameters & History */}
          <div className="w-full lg:w-[420px] flex flex-col gap-4">
            
            {/* Parameters Card */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[600px]">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
                <div className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-amber-500"/>
                    <h3 className="text-xs font-black text-gray-950 uppercase tracking-tight" style={{ fontWeight: 1000 }}>Parameters</h3>
                </div>
                {/* Recently Used / Custom toggle hidden for now */}
              </div>

              <div className="flex-1 overflow-y-auto pr-1.5 custom-scrollbar space-y-3">
                
                {/* Parameters Inputs */}
                <div className="grid grid-cols-1 gap-1.5 pt-0.5">
                    {labelMapArray.map((item) => (
                         <div key={item.key} className="flex items-center justify-between gap-3 py-0 border-b border-gray-50 pb-1">
                            <label className="text-[11px] font-black text-gray-700 leading-tight tracking-tight flex-1" style={{ fontWeight: 1000 }}>{item.label}</label>
                            <input 
                              type="text" 
                              name={item.key} 
                              value={inputs[item.key]} 
                              onChange={handleInputChange} 
                              placeholder={defaultValues[item.key]}
                              className="w-24 text-[11px] font-black border-gray-200 rounded-md py-1 px-2.5 border text-right focus:ring-2 focus:ring-amber-200 outline-none shadow-sm" 
                              style={{ fontWeight: 900 }} 
                            />
                        </div>
                    ))}
                </div>

                <div className="pt-2">
                   <label className="block text-[11px] font-black text-gray-950 uppercase tracking-widest pl-1 mb-2" style={{ fontWeight: 1000 }}>Prev Day Battery Profile</label>
                   <div className="flex items-center gap-2">
                       <input 
                         type="text" 
                         name="prev_day_battery_profile" 
                         value={inputs.prev_day_battery_profile} 
                         onChange={handleInputChange} 
                         placeholder="[0.0, 0.0, ...]"
                         className="flex-1 text-[10px] font-black border-gray-200 rounded-md py-2.5 px-3 border font-mono bg-white focus:ring-2 focus:ring-amber-200 outline-none shadow-sm" 
                         style={{ fontWeight: 900 }} 
                       />
                       <button
                         type="button"
                         onClick={() => setIsProfileModalOpen(true)}
                         className="p-2.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-300 text-amber-600 hover:text-amber-700 transition-all rounded-md shadow-sm shrink-0 flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-wider"
                         style={{ fontWeight: 1000 }}
                         title="Edit Grid"
                       >
                         <Pencil size={12} />
                         <span>Edit</span>
                       </button>
                   </div>
                </div>

                {/* File Uploads Section */}
                <div className="space-y-2 pt-2">
                    <h4 className="text-[10px] font-black text-gray-950 uppercase tracking-widest pl-1" style={{ fontWeight: 1000 }}>Excel Files Tracking</h4>
                    <div className="grid grid-cols-1 gap-2.5">
                        <div className="border border-gray-100 rounded-lg p-2.5 bg-gray-50/50 flex items-center justify-between shadow-sm">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    {/* <FileText className="w-3.5 h-3.5 text-[#0066A3] shrink-0" /> */}
                                    <p className="text-[11px] font-black uppercase text-gray-900 leading-tight" style={{ fontWeight: 1000 }}>Input Power</p>
                                </div>
                                {fileRaw ? (
                                    <p className="text-[9px] text-emerald-600 font-bold truncate flex items-center mt-0.5">
                                        <CheckCircle className="w-2.5 h-2.5 mr-1 shrink-0" />
                                        {fileRaw.name}
                                    </p>
                                ) : (recentRawName && fillMode === 'recent') ? (
                                    <p className="text-[8px] text-gray-500 font-bold truncate flex items-center mt-0.5">
                                        <History className="w-2.5 h-2.5 mr-1 shrink-0" />
                                        {recentRawName}
                                    </p>
                                ) : null}
                            </div>
                            <button onClick={() => rawFileInputRef.current?.click()} className="px-3.5 py-1.5 bg-white border border-gray-200 rounded-md text-[10px] font-black text-gray-700 hover:bg-gray-50 shadow-sm shrink-0 ml-2" style={{ fontWeight: 1000 }}>Choose</button>
                            <input type="file" ref={rawFileInputRef} className="hidden" accept={ACCEPTED_UPLOAD_TYPES} onChange={(e) => { const f = e.target.files?.[0]; if (f) setFileRaw(f); }} />
                        </div>
                        <div className="border border-gray-100 rounded-lg p-2.5 bg-gray-50/50 flex items-center justify-between shadow-sm">
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-black uppercase text-gray-900 leading-tight" style={{ fontWeight: 1000 }}>Exchange Rate</p>
                                {(exchangeStartDate && exchangeEndDate) ? (
                                  <p className="text-[9px] text-emerald-600 font-bold truncate flex items-center mt-0.5">
                                    <CheckCircle className="w-2.5 h-2.5 mr-1 shrink-0" />
                                    {exchangeStartDate} to {exchangeEndDate}
                                  </p>
                                ) : (recentDamName && fillMode === 'recent') ? (
                                  <p className="text-[8px] text-gray-500 font-bold truncate flex items-center mt-0.5">
                                    <History className="w-2.5 h-2.5 mr-1 shrink-0" />
                                    Last generated: {recentDamName}
                                  </p>
                                ) : null}
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <input
                                    type="date"
                                    value={exchangeStartDate}
                                    onChange={(e) => setExchangeStartDate(e.target.value)}
                                    className="text-[10px] font-black border-gray-200 rounded-md py-1.5 px-2 border focus:ring-2 focus:ring-amber-200 outline-none shadow-sm"
                                    title="Exchange start date"
                                  />
                                  <input
                                    type="date"
                                    value={exchangeEndDate}
                                    onChange={(e) => setExchangeEndDate(e.target.value)}
                                    className="text-[10px] font-black border-gray-200 rounded-md py-1.5 px-2 border focus:ring-2 focus:ring-amber-200 outline-none shadow-sm"
                                    title="Exchange end date"
                                  />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

              </div>

              {/* Run Button Container */}
              <div className="pt-4 border-t border-gray-100">
                <button 
                  onClick={handleRunSimulation} 
                  disabled={status.status === 'running' || !!actionsDisabled || (tokenBalance !== null && tokenBalance < tokenCost)} 
                  className="w-full py-4 bg-amber-500 hover:bg-amber-600 active:scale-[0.98] transition-all text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-amber-200 flex items-center justify-center gap-2"
                  title={actionsDisabled ? 'Only HO Admin can execute simulation tabs.' : (tokenBalance !== null && tokenBalance < tokenCost) ? `Insufficient tokens. Required: ${tokenCost}, Available: ${tokenBalance ?? 0}` : 'Run Simulation'}
                >
                  {status.status === 'running' && <Loader2 size={18} className="animate-spin" />}
                  <span>{status.status === 'running' ? 'PROCESSING SIMULATION...' : 'RUN SIMULATION'}</span>
                  {status.status !== 'running' && (
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-white/20">
                      {tokenBalance === null ? 'UNLIMITED' : `${tokenBalance ?? 0}/${tokenCost}`} TOKENS
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* History Card (Below Parameters) */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-h-[350px]">
              <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <History className="w-4 h-4 text-amber-500"/>
                    <h3 className="text-xs font-black text-gray-900 uppercase tracking-wide" style={{ fontWeight: 1000 }}>Run History</h3>
                </div>
                <button onClick={() => loadHistory()} className="text-[10px] font-black text-amber-600 hover:underline">REFRESH</button>
              </div>
              <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto custom-scrollbar">
                {history.length === 0 ? <p className="p-10 text-center text-[10px] text-gray-400 font-bold">No previous runs</p> : history.map(run => (
                  <div key={run.id} onClick={() => handleRunClick(run)} className={`p-4 cursor-pointer hover:bg-amber-50 transition-colors ${selectedRunId === run.run_id ? 'bg-amber-50 border-l-4 border-amber-500' : ''}`}>
                    <div className="flex justify-between items-start mb-1.5 gap-4">
                      <p className="text-[11px] font-black text-gray-950 truncate flex-1" style={{ fontWeight: 1000 }}>{run.run_id}</p>
                      <div className="flex items-center gap-2">
                        {run.status === 'completed' && (
                          <button onClick={(e) => { e.stopPropagation(); exportRunCsv(run.run_id); }} className="p-1.5 hover:bg-amber-100 rounded-md text-gray-400 hover:text-amber-600 transition-all" title="Export SQL CSV"><Download className="w-3.5 h-3.5"/></button>
                        )}
                        <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase ${run.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#E3F0F9] text-[#1C4D88]'}`} style={{ fontWeight: 1000 }}>{run.status}</span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); deleteRun(run.run_id); }} 
                          className="p-1.5 hover:bg-red-50 rounded-md text-gray-400 hover:text-red-600 transition-all" 
                          title="Delete Run History"
                        >
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    </div>
                    <p className="text-[10px] font-black text-gray-400">{run.started_at ? new Date(run.started_at).toLocaleString() : 'N/A'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: GRAPHS (Same height as Parameters) */}
          <div className="flex-1 min-w-0 h-[600px]">
             <RtcCharts data={chartData} loading={loadingCharts} title={`RTC Energy Profile`} />
          </div>

        </div>
      </div>

      {/* Battery Profile Grid Editor Modal */}
      {isProfileModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-black text-gray-900 tracking-tight flex items-center gap-2" style={{ fontWeight: 900 }}>
                  <div className="w-6 h-6 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Pencil size={12} className="text-white" />
                  </div>
                  Edit Battery Profile
                  {/* <span className="text-[10px] font-semibold text-gray-400 ml-1">96 Timeblocks</span> */}
                </h3>
                <p className="text-[10px] text-gray-400 mt-0.5 pl-8">15-minute intervals across a 24-hour day</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsProfileModalOpen(false)}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X size={16} />
              </button>
            </div>

            {/* Toolbar */}
            <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
              {/* Left: Import Actions */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <input 
                  type="text"
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                  placeholder="Paste values separated by spaces or commas..."
                  className="flex-1 min-w-[180px] text-[11px] font-mono border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 outline-none bg-gray-50 h-8"
                />
                <button
                  type="button"
                  onClick={handleBulkImport}
                  className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-[10px] font-bold tracking-wide shrink-0 h-8 transition-colors"
                >
                  Import
                </button>
              </div>

              {/* Right: File Actions & Resets */}
              <div className="flex items-center gap-1.5">
                <input 
                  type="file" 
                  ref={profileFileInputRef}
                  accept=".json,.txt,.csv,.xlsx,.xls"
                  onChange={handleProfileFileUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => profileFileInputRef.current?.click()}
                  className="px-2.5 py-1.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-lg text-[11px] tracking-wide shrink-0 flex items-center gap-1.5 h-8 transition-colors"
                  style={{ fontWeight: 900 }}
                  title="Upload .xlsx, .json, .txt, or .csv file"
                >
                  <Upload size={12} />
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const rows = Array.from({ length: 96 }).map((_, i) => {
                      const hr = Math.floor(i / 4);
                      const min = (i % 4) * 15;
                      const time = `${hr.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
                      return { BLOCK: i + 1, TIME: time, VALUE: parseFloat(profileValues[i] || '0') };
                    });
                    const ws = XLSX.utils.json_to_sheet(rows);
                    ws['!cols'] = [{ wch: 10 }, { wch: 12 }, { wch: 15 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Battery Profile');
                    XLSX.writeFile(wb, 'battery_profile_template.xlsx');
                  }}
                  className="px-2.5 py-1.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 rounded-lg text-[11px] tracking-wide shrink-0 flex items-center gap-1.5 h-8 transition-colors"
                  style={{ fontWeight: 900 }}
                  title="Download Excel template with current values"
                >
                  <Download size={12} />
                  Template
                </button>

                <div className="w-px h-5 bg-gray-200 mx-1"></div>

                <button 
                  type="button"
                  onClick={() => setProfileValues(DEFAULT_BATTERY_PROFILE.map(String))}
                  className="px-2.5 py-1.5 bg-white hover:bg-amber-50 border border-gray-200 text-gray-600 hover:text-amber-700 rounded-lg text-[11px] tracking-wide shrink-0 h-8 transition-colors"
                  style={{ fontWeight: 900 }}
                >
                  Load Default
                </button>
                <button 
                  type="button"
                  onClick={() => setProfileValues(Array(96).fill('0.0'))}
                  className="px-2.5 py-1.5 bg-white hover:bg-red-50 border border-gray-200 text-gray-600 hover:text-red-600 rounded-lg text-[11px] tracking-wide shrink-0 h-8 transition-colors"
                  style={{ fontWeight: 900 }}
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Grid of Columns */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map((colIdx) => {
                  const start = colIdx * 24;
                  const end = start + 24;
                  const hourStart = colIdx * 6;
                  const hourEnd = hourStart + 6;
                  return (
                    <div key={colIdx} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm flex flex-col">
                      <div className="bg-gray-100 px-2.5 py-1.5 border-b border-gray-200 flex justify-between items-center shrink-0">
                        <span className="text-[10px] font-black text-gray-800 uppercase tracking-wider" style={{ fontWeight: 1000 }}>
                          {hourStart.toString().padStart(2, '0')}:00 - {hourEnd === 24 ? '24:00' : `${hourEnd.toString().padStart(2, '0')}:00`}
                        </span>
                        <span className="text-[9px] font-black text-gray-800 uppercase" style={{ fontWeight: 1000 }}>Blocks {start + 1}-{end}</span>
                      </div>
                      <div className="flex-1 overflow-y-auto max-h-[46vh] custom-scrollbar relative">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-100 text-[10px] font-black text-gray-800 uppercase tracking-widest select-none shrink-0">
                              <th className="py-1 px-2 text-center w-10 bg-gray-50" style={{ fontWeight: 1000 }}>Block</th>
                              <th className="py-1 px-2 bg-gray-50" style={{ fontWeight: 1000 }}>Time</th>
                              <th className="py-1 px-2 text-right w-20 bg-gray-50" style={{ fontWeight: 1000 }}>Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {Array.from({ length: 24 }).map((_, localIdx) => {
                              const idx = start + localIdx;
                              const blockNum = idx + 1;
                              const hr = Math.floor(idx / 4);
                              const min = (idx % 4) * 15;
                              const timestamp = `${hr.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
                              return (
                                <tr key={idx} className="hover:bg-amber-50/20 text-[11px] h-7">
                                  <td className="py-0.5 px-2 text-center font-black text-gray-900 select-none" style={{ fontWeight: 1000 }}>{blockNum}</td>
                                  <td className="py-0.5 px-2 font-black text-gray-900 select-none" style={{ fontWeight: 1000 }}>{timestamp}</td>
                                  <td className="py-0.5 px-2 text-right">
                                    <input 
                                      type="text"
                                      value={profileValues[idx] || '0.0'}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setProfileValues(prev => {
                                          const next = [...prev];
                                          next[idx] = val;
                                          return next;
                                        });
                                      }}
                                      className="w-16 px-1.5 py-0.5 text-right font-black border border-gray-200 rounded focus:ring-1 focus:ring-amber-400 outline-none text-[10px] bg-white h-5"
                                      style={{ fontWeight: 900 }}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-2.5 border-t border-gray-200 flex items-center justify-between shrink-0">
              <span className="text-[11px] text-gray-400 font-medium">
                {profileValues.length} / 96 blocks
              </span>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  onClick={() => setIsProfileModalOpen(false)}
                  className="px-4 py-1.5 bg-white hover:bg-gray-50 border border-gray-200 text-[11px] font-semibold text-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={handleSaveProfile}
                  className="px-4 py-1.5 bg-gray-900 hover:bg-gray-800 text-[11px] font-semibold text-white rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Check size={13} /> Save & Apply
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default RtcSimulationSection;

